<template>
  <nav
    :class="['sb-sidebar', isOpen ? 'sb-sidebar-open' : '']"
    aria-label="Main navigation"
    @click.stop
    @mouseenter="startHover"
    @mouseleave="endHover"
  >
    <!-- Hamburger — same height as topbar for visual alignment -->
    <button
      :class="['sb-hamburger', isOpen ? 'sb-sidebar-open' : '']"
      :title="isOpen ? 'Close this panel' : 'Toggle menu'"
      @click.stop="toggleDrawer"
    >
      <span class="material-icons-outlined">{{
        isOpen ? 'close' : 'menu'
      }}</span>
    </button>
    <div class="sb-sidebar-divider"></div>

    <!-- Nav items — icon only, derived from the same navBarGroups data -->
    <div class="sb-nav-list">
      <template v-for="(group, idx) in navBarGroups">
        <div
          v-for="(item, jdx) in group"
          :key="`ng-${idx}-${jdx}`"
          class="sb-nav-group"
        >
          <!-- Items with sub-items: button that toggles expansion on click -->
          <button
            v-if="item.subModules && item.subModules.length"
            :id="item.id || undefined"
            :title="item.title"
            :aria-label="item.title"
            :aria-current="highlightActiveLink(item) ? 'page' : undefined"
            :class="[
              'sb-nav-item',
              highlightActiveLink(item) ? 'active' : '',
              expandedGroup === idx + '-' + jdx ? 'sb-nav-item-expanded' : '',
            ]"
            @click.stop="toggleGroup(idx + '-' + jdx)"
          >
            <svg
              v-if="item.customSvg"
              class="sb-nav-icon-svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle
                cx="9"
                cy="9"
                r="3"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              ></circle>
              <circle cx="3" cy="3" r="1.5" fill="currentColor"></circle>
              <circle cx="15" cy="3" r="1.5" fill="currentColor"></circle>
              <circle cx="3" cy="15" r="1.5" fill="currentColor"></circle>
              <circle cx="15" cy="15" r="1.5" fill="currentColor"></circle>
              <line
                x1="4.06"
                y1="4.06"
                x2="7.17"
                y2="7.17"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="13.94"
                y1="4.06"
                x2="10.83"
                y2="7.17"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="4.06"
                y1="13.94"
                x2="7.17"
                y2="10.83"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="13.94"
                y1="13.94"
                x2="10.83"
                y2="10.83"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
            </svg>
            <span v-else class="material-icons-outlined">{{
              item.iconText
            }}</span>
            <span class="sb-nav-label">{{ item.title }}</span>
            <span class="material-icons-outlined sb-nav-chevron"
              >expand_more</span
            >
          </button>
          <!-- Items without sub-items: router-link or anchor -->
          <router-link
            v-else-if="item.useRouter && item.link"
            :id="item.id || undefined"
            :to="item.link"
            :title="item.title"
            :aria-label="item.title"
            :aria-current="highlightActiveLink(item) ? 'page' : undefined"
            :class="['sb-nav-item', highlightActiveLink(item) ? 'active' : '']"
            @click.native="closeDrawer"
          >
            <svg
              v-if="item.customSvg"
              class="sb-nav-icon-svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle
                cx="9"
                cy="9"
                r="3"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              ></circle>
              <circle cx="3" cy="3" r="1.5" fill="currentColor"></circle>
              <circle cx="15" cy="3" r="1.5" fill="currentColor"></circle>
              <circle cx="3" cy="15" r="1.5" fill="currentColor"></circle>
              <circle cx="15" cy="15" r="1.5" fill="currentColor"></circle>
              <line
                x1="4.06"
                y1="4.06"
                x2="7.17"
                y2="7.17"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="13.94"
                y1="4.06"
                x2="10.83"
                y2="7.17"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="4.06"
                y1="13.94"
                x2="7.17"
                y2="10.83"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="13.94"
                y1="13.94"
                x2="10.83"
                y2="10.83"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
            </svg>
            <span v-else class="material-icons-outlined">{{
              item.iconText
            }}</span>
            <span class="sb-nav-label">{{ item.title }}</span>
          </router-link>
          <a
            v-else-if="item.link"
            :id="item.id || undefined"
            :href="item.link"
            :title="item.title"
            :aria-label="item.title"
            :aria-current="highlightActiveLink(item) ? 'page' : undefined"
            :class="['sb-nav-item', highlightActiveLink(item) ? 'active' : '']"
            @click="closeDrawer"
          >
            <svg
              v-if="item.customSvg"
              class="sb-nav-icon-svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle
                cx="9"
                cy="9"
                r="3"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              ></circle>
              <circle cx="3" cy="3" r="1.5" fill="currentColor"></circle>
              <circle cx="15" cy="3" r="1.5" fill="currentColor"></circle>
              <circle cx="3" cy="15" r="1.5" fill="currentColor"></circle>
              <circle cx="15" cy="15" r="1.5" fill="currentColor"></circle>
              <line
                x1="4.06"
                y1="4.06"
                x2="7.17"
                y2="7.17"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="13.94"
                y1="4.06"
                x2="10.83"
                y2="7.17"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="4.06"
                y1="13.94"
                x2="7.17"
                y2="10.83"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
              <line
                x1="13.94"
                y1="13.94"
                x2="10.83"
                y2="10.83"
                stroke="currentColor"
                stroke-width="1.3"
              ></line>
            </svg>
            <span v-else class="material-icons-outlined">{{
              item.iconText
            }}</span>
            <span class="sb-nav-label">{{ item.title }}</span>
          </a>
          <!-- Sub-items: shown when this group is clicked open -->
          <a
            v-for="(sub, sidx) in item.subModules || []"
            v-show="expandedGroup === idx + '-' + jdx"
            :key="`sub-${idx}-${jdx}-${sidx}`"
            :href="sub.href"
            :title="sub.text"
            :class="[
              'sb-nav-sub-item',
              currentLink.includes(sub.href.split('#')[0].toLowerCase())
                ? 'active'
                : '',
            ]"
            @click="closeDrawer"
            >{{ sub.text }}</a
          >
        </div>
        <div
          v-if="idx > 0 && idx < navBarGroups.length - 1"
          :key="`nd-${idx}`"
          class="sb-sidebar-divider"
        ></div>
      </template>
    </div>

    <!-- Pushes the settings icon to the bottom -->
    <div class="nav-spacer"></div>

    <div v-if="isAdmin" class="sb-sidebar-divider"></div>

    <!-- Settings at bottom -->
    <a
      v-if="isAdmin"
      :class="['sb-nav-item', currentLink.includes('/admin') ? 'active' : '']"
      :aria-current="currentLink.includes('/admin') ? 'page' : undefined"
      title="Administration"
      aria-label="Administration"
      :href="`${SbBaseUrl}/Admin`"
      @click="closeDrawer"
    >
      <span class="material-icons-outlined">settings</span>
      <span class="sb-nav-label">Administration</span>
    </a>
  </nav>
</template>

<script>
import moduleDetails from '../pages/HomeModules.mixin.js';

export default {
  name: 'MainLayoutSidebar',
  mixins: [moduleDetails],
  props: {
    // Kept for backward compatibility but no longer drives sidebar width
    width: {
      type: Number,
      default() {
        return 48;
      },
    },
    mountedInOldLayout: {
      type: Boolean,
    },
  },
  data() {
    return {
      drawer: false,
      hovering: false,
      manuallyClosed: false,
      expandedGroup: null,
      hoverTimer: null,
      homeLink: {
        title: '',
        link: this.SbBaseUrl + '/Home#/',
        iconText: 'home',
        useRouter: false,
      },
      moduleLinks: [],
    };
  },
  computed: {
    isOpen() {
      return this.drawer || this.hovering;
    },
    isAdmin() {
      return this.$store.getters.userIsAdmin;
    },
    isUserOnly() {
      return (
        !this.$store.getters.userIsAdmin && !this.$store.getters.userIsAnalyst
      );
    },
    navBarGroups() {
      if (this.isUserOnly) return [this.moduleLinks];
      return [[this.homeLink], this.moduleLinks];
    },
    currentLink() {
      let path = window.location.pathname
        .toLowerCase()
        .replace('/home/index', '')
        .replace(/\/$/, '');
      return (window.location.origin + path).toLowerCase();
    },
  },
  async mounted() {
    document.addEventListener('click', this.closeDrawer);
    await this.$store.dispatch('FETCH_ENTITLEMENTS');
    this.homeLink.title = 'Identity Hub';
    this.buildModuleLinks();
    this.moduleLinks = this.getUserModules();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDrawer);
  },
  methods: {
    /* Hamburger button: open if closed, close+lock if open. */
    toggleDrawer() {
      if (this.isOpen) {
        this.drawer = false;
        this.hovering = false;
        this.manuallyClosed = true;
        clearTimeout(this.hoverTimer);
      } else {
        this.drawer = true;
        this.manuallyClosed = false;
      }
    },

    /* Shared close for nav-item clicks. Clears hover state so the sidebar
       actually collapses even if the cursor is still inside. */
    closeDrawer() {
      this.drawer = false;
      this.hovering = false;
      clearTimeout(this.hoverTimer);
    },

    /* Hover open: skipped entirely if the user just manually closed. */
    startHover() {
      if (this.manuallyClosed) return;
      this.hoverTimer = setTimeout(() => {
        this.hovering = true;
      }, 600);
    },

    /* Hover end: reset manuallyClosed so the next hover works normally. */
    endHover() {
      clearTimeout(this.hoverTimer);
      this.hovering = false;
      this.manuallyClosed = false;
      this.expandedGroup = null;
    },

    toggleGroup(key) {
      this.expandedGroup = this.expandedGroup === key ? null : key;
    },

    /**
     * Returns the href to use for a nav item.
     * Items with subModules link to the first subModule's href.
     */
    getItemLink(item) {
      if (item.link) return item.link;
      if (item.subModules && item.subModules.length > 0) {
        return item.subModules[0].href;
      }
      return null;
    },

    generateModuleObject(module, separateLinks = false) {
      let modules = [];
      if (module.links.length > 0) {
        if (separateLinks) {
          for (let i = 0; i < module.links.length; i++) {
            modules.push({
              title: module.links[i].text,
              link: module.links[i].href,
              iconText: module.links[i].icon,
              customSvg: module.links[i].customSvg || false,
              useRouter: false,
              active: false,
            });
          }
        } else {
          modules.push({
            title: module.title,
            iconText: module.icon,
            subModules: module.links,
            useRouter: false,
            active: false,
          });
          for (let j = 0; j < module.links.length; j++) {
            if (module.links[j].subLinks) {
              modules[0].subModules.push({
                href: module.links[j].subLinks[0].href,
                text: module.links[j].subLinks[0].text,
                iconText: 'trending_flat',
              });
            }
          }
        }
        return modules;
      }
    },
    // eslint-disable-next-line complexity
    getUserModules() {
      let userModules = [],
        moduleObjects = [];
      moduleObjects = this.generateModuleObject(this.myDashboard, true);
      if (moduleObjects) userModules = userModules.concat(moduleObjects);
      moduleObjects = this.generateModuleObject(this.ownershipManagement);
      if (moduleObjects) userModules = userModules.concat(moduleObjects);
      moduleObjects = this.generateModuleObject(this.identityMap, true);
      if (moduleObjects) userModules = userModules.concat(moduleObjects);
      moduleObjects = this.generateModuleObject(this.accounts);
      if (moduleObjects) {
        if (
          moduleObjects.length === 1 &&
          moduleObjects[0].subModules &&
          moduleObjects[0].subModules.length === 1
        ) {
          moduleObjects[0].link = moduleObjects[0].subModules[0].href;
          delete moduleObjects[0].subModules;
        }
        userModules = userModules.concat(moduleObjects);
      }
      moduleObjects = this.generateModuleObject(this.groups);
      if (moduleObjects) {
        // if there's only one sub-item, remove it so the main item is a link
        if (
          moduleObjects.length === 1 &&
          moduleObjects[0].subModules &&
          moduleObjects[0].subModules.length === 1
        ) {
          moduleObjects[0].link = moduleObjects[0].subModules[0].href;
          delete moduleObjects[0].subModules;
        }
        userModules = userModules.concat(moduleObjects);
      }
      moduleObjects = this.generateModuleObject(this.data);
      if (moduleObjects) userModules = userModules.concat(moduleObjects);
      moduleObjects = this.generateModuleObject(this.hygieneCenter);
      if (moduleObjects) {
        const subs = moduleObjects[0].subModules;
        // Only show when ARM is accessible (it is always the first link).
        // With multiple sub-items keep the group; with one sub-item (User role)
        // promote it to a direct link instead of hiding it.
        if (subs && subs.length > 1) {
          userModules = userModules.concat(moduleObjects[0]);
        } else if (subs && subs.length === 1) {
          moduleObjects[0].link = subs[0].href;
          moduleObjects[0].title = subs[0].text;
          delete moduleObjects[0].subModules;
          userModules = userModules.concat(moduleObjects[0]);
        }
      }
      return userModules;
    },
    // eslint-disable-next-line complexity
    highlightActiveLink(item) {
      if (!item.useRouter) {
        let highLight = false;
        let link = item.link || item.href || this.getItemLink(item);
        if (link) {
          let url = link.toLowerCase();
          url = url.split('#')[0];
          if (
            this.currentLink == url ||
            this.checkHighlightActiveLink(item, url)
          ) {
            highLight = true;
          }
        } else {
          if (this.checkHighlightActiveLink(item, undefined)) {
            highLight = true;
          }
        }
        return highLight;
      }
    },

    checkHighlightActiveLink(item, url) {
      return (
        (url &&
          this.currentLink.includes(url) &&
          item.title != 'Identity Hub' &&
          !this.currentLink.includes('/beta')) ||
        (item.subModules &&
          item.subModules.some((ele) =>
            this.currentLink.includes(ele.href.split('#')[0].toLowerCase())
          ))
      );
    },
  },
};
</script>

<style>
/* Keep a subtle visual gap between adjacent nav item backgrounds */
.sb-nav-list {
  gap: 2px;
}

.sb-sidebar.sb-sidebar-open .sb-nav-group {
  gap: 2px;
}
</style>
